import { FC } from "react";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../components/fonts/font-props";
import Card from "../../../../../components/layouts/card/card";
import customizationStore from "../../../../../stores/customizations/customization-store";
import { FormField } from "../../../../../components/form-field/form-field";
import { PORTAL_BACKGROUND_SIZE, PORTAL_BACKGROUND_TYPE } from "../../../../../models/User";
import backgrounds_portal_edge_preview from "../../../../../images/previews/backgrounds-portal-edge.svg";
import ControlGroupWithPreview from "../../../../../components/layouts/control-group-with-preview/control-group-with-preview";
import ListLayout from "../../../../../components/layouts/list-layout/list-layout";
import { FormFieldLine } from "../../../../../components/form-field-line/form-field-line";
import { observer } from "mobx-react";
import { IOption, SelectDropdown } from "../../../../../components/inputs/select-dropdown/select-dropdown";
import ColorPickerInput from "../../color-picker-input/color-picker-input";
import { Input } from "../../../../../components/inputs/input/input";
import IconImageRequest from "../../../../../components/icons/icon-image-request/icon-image-request";
import FileUpload from "../../../../../components/file-upload/file-upload";
import { ButtonIcon } from "../../../../../components/inputs/button-icon/button-icon";
import IconDelete from "../../../../../components/icons/icon-delete/icon-delete";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import "./background-upload.scss";
import "./background-preview.scss";
import Hr from "../../../../../components/hr/hr";
import { isSet } from "../../../../../models/model-types-methods";

interface IProps {
    onExpandClick: (expanded: boolean) => void;
    isExpanded: boolean;
}

const PortalEdgeBackgroundCard: FC<IProps> = observer(({ isExpanded, onExpandClick }) => {
    const backgroundTypeOptions: IOption[] = [
        { label: "Color", value: PORTAL_BACKGROUND_TYPE.COLOR },
        { label: "Image", value: PORTAL_BACKGROUND_TYPE.IMAGE },
    ];

    const edgeCustomizationStore = customizationStore.getEdgeStore();

    const getBackgroundTypeValue = (value: PORTAL_BACKGROUND_TYPE | undefined): IOption | undefined => {
        return backgroundTypeOptions.find((option) => option.value === value);
    };

    const backgroundSizeOptions: IOption[] = [
        { label: "Stretch", value: PORTAL_BACKGROUND_SIZE.STRETCH },
        { label: "Repeat", value: PORTAL_BACKGROUND_SIZE.REPEAT },
    ];

    const getBackgroundSizeValue = (value: PORTAL_BACKGROUND_SIZE): IOption | undefined => {
        return backgroundSizeOptions.find((option) => option.value === value);
    };

    const uploadReturnPortalBackgroundImage = (
        fileData: File,
        fileContent: string
    ) => {
        if (fileData && fileContent) {
            edgeCustomizationStore.uploadReturnPortalBackgroundImage(fileData.type, fileContent.split(",")[1]);
        }
    };

    const uploadReturnPortalCoverImage = (fileData: File, fileContent: string) => {
        if (fileData && fileContent) {
            edgeCustomizationStore.uploadReturnPortalCoverImage(fileData.type, fileContent.split(",")[1]);
        }
    };

    return (
        <Card
            toolbar={{
                title: (
                    <FontBody1
                        color={FONT_COLOR.PRIMARY2}
                        variant={FONT_VARIANT.BOLD}
                    >
                        Backgrounds
                    </FontBody1>
                ),
            }}
            isExpanded={isExpanded}
            expandCallback={onExpandClick}
        >
            <FormField
                label="Return portal"
                instructions="Customize the background of your return portal"
                children={null}
            />
            <ControlGroupWithPreview
                hasTopBorder={false}
                gridStyle={{
                    childrenWidth: { width: 1 },
                    previewWidth: { width: 1 },
                }}
                preview={
                    <div className="preview">
                        <img src={backgrounds_portal_edge_preview} alt="" />
                    </div>
                }
            >
                <ListLayout>
                    <FormField label="Page background" children={null} />
                    <FormFieldLine label="Page background type">
                        <SelectDropdown
                            options={backgroundTypeOptions}
                            value={isSet(edgeCustomizationStore.returnPortalBackgroundType) ? getBackgroundTypeValue(edgeCustomizationStore.returnPortalBackgroundType) : undefined}
                            onChange={(value) => edgeCustomizationStore.setReturnPortalBackgroundType(value?.value as PORTAL_BACKGROUND_TYPE)}
                        />
                    </FormFieldLine>
                    {edgeCustomizationStore.returnPortalBackgroundType === PORTAL_BACKGROUND_TYPE.COLOR && (
                        <FormFieldLine label="Page background color">
                            <ColorPickerInput
                                value={edgeCustomizationStore.returnPortalBackgroundValue}
                                onChange={edgeCustomizationStore.setReturnPortalBackgroundValue}
                            />
                        </FormFieldLine>
                    )}
                    {edgeCustomizationStore.returnPortalBackgroundType === PORTAL_BACKGROUND_TYPE.IMAGE && (
                        <>
                            <div className="background-upload-container">
                                <FormFieldLine label="Page background image">
                                    <div className="background-upload-input">
                                        <div className="upload-icon-container">
                                            <IconImageRequest color="var(--color-semantic-3)" />
                                        </div>
                                        <FileUpload
                                            uploadFile={(fileData, fileContent) => uploadReturnPortalBackgroundImage(fileData, fileContent as string)}
                                            buttonText={"Upload image"}
                                            readType="dataUrl"
                                            fileTypes={["image/jpeg", "image/png", "image/gif",]}
                                            acceptedFileFormats=".jpg,.jpeg,.png,.gif"
                                            disabled={edgeCustomizationStore.isSaving()}
                                        />
                                    </div>
                                </FormFieldLine>
                                {edgeCustomizationStore.returnPortalBackgroundValue && (
                                    <div className="background-preview-container">
                                        <FontBody2 color={FONT_COLOR.PRIMARY3_50}>
                                            Image size {edgeCustomizationStore.returnPortalBackgroundImageDimensions}
                                        </FontBody2>
                                        <div className="background">
                                            <img src={edgeCustomizationStore.returnPortalBackgroundValue} alt="background"/>
                                            <ButtonIcon
                                                Component={IconDelete}
                                                color="var(--color-accent-3)"
                                                onClick={edgeCustomizationStore.deleteReturnPortalBackgroundImage}
                                                disabled={edgeCustomizationStore.isSaving()}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                            <FormFieldLine label="Page background size">
                                <SelectDropdown
                                    options={backgroundSizeOptions}
                                    value={getBackgroundSizeValue(edgeCustomizationStore.returnPortalBackgroundSize)}
                                    onChange={(value) => edgeCustomizationStore.setReturnPortalBackgroundSize(value?.value as PORTAL_BACKGROUND_SIZE)}
                                />
                            </FormFieldLine>
                        </>
                    )}

                    <Hr />

                    <FormField
                        label="Portal Background"
                        children={null}
                    />
                    <FormFieldLine label="Portal background">
                        <ColorPickerInput
                            value={edgeCustomizationStore.returnPortalForegroundColor}
                            onChange={edgeCustomizationStore.setReturnPortalForegroundColor}
                        />
                    </FormFieldLine>
                    <FormFieldLine label="Portal background opacity">
                        <Input
                            type="number"
                            min={0}
                            max={100}
                            value={edgeCustomizationStore.returnPortalForegroundOpacity}
                            onChange={edgeCustomizationStore.setReturnPortalForegroundOpacity}
                        />
                    </FormFieldLine>

                    <Hr />

                    <FormField
                        label="Secondary Background"
                        children={null}
                    />
                    <FormFieldLine label="Secondary background">
                        <ColorPickerInput
                            value={edgeCustomizationStore.portalSecondaryBackgroundColor}
                            onChange={edgeCustomizationStore.setPortalSecondaryBackgroundColor}
                        />
                    </FormFieldLine>
                    <FormFieldLine label="Secondary Background Opacity">
                        <Input
                            type="number"
                            min={0}
                            max={100}
                            value={edgeCustomizationStore.portalSecondaryBackgroundOpacity}
                            onChange={edgeCustomizationStore.setPortalSecondaryBackgroundOpacity}
                        />
                    </FormFieldLine>

                    <Hr />

                    <FormField
                        label="Stroke Lines"
                        children={null}
                    />
                    <FormFieldLine label="Stroke / line color">
                        <ColorPickerInput
                            value={edgeCustomizationStore.strokeColor}
                            onChange={edgeCustomizationStore.setStrokeColor}
                        />
                    </FormFieldLine>

                    <Hr />

                    <FormField label="Shadows" children={null} />
                    <FormFieldLine label="Popup Overlay">
                        <ColorPickerInput
                            value={edgeCustomizationStore.popupOverlayColor}
                            onChange={edgeCustomizationStore.setPopupOverlayColor}
                        />
                    </FormFieldLine>
                    <FormFieldLine label="Popup Overlay Opacity">
                        <Input
                            type="number"
                            min={0}
                            max={100}
                            value={edgeCustomizationStore.popupOverlayOpacity}
                            onChange={edgeCustomizationStore.setPopupOverlayOpacity}
                        />
                    </FormFieldLine>
                    <FormFieldLine label="Drop Shadow">
                        <ColorPickerInput
                            value={edgeCustomizationStore.dropShadowColor}
                            onChange={edgeCustomizationStore.setDropShadowColor}
                        />
                    </FormFieldLine>
                    <FormFieldLine label="Drop Shadow Opacity">
                        <Input
                            type="number"
                            min={0}
                            max={100}
                            value={edgeCustomizationStore.dropShadowOpacity}
                            onChange={edgeCustomizationStore.setDropShadowOpacity}
                        />
                    </FormFieldLine>

                    <Hr />

                    <FormField
                        label="Cover Image"
                        children={null}
                    />
                    <div className="background-upload-form-field">
                        <FormFieldLine
                            label="Portal Cover Image (optional)"
                            note={
                                <div className="form-field-line-note">
                                    <p>The image that appears on the first screen of the portal. For optimal results, follow these guidelines:</p>
                                    <ul>
                                        <li>minimum 500px width and 456px height</li>
                                        <li>File size: Up to 400KB</li>
                                        <li>Formats: JPG, PNG, or WebP</li>
                                    </ul>
                                </div>
                            }
                        >
                            <div className="background-upload-container">
                                <div className="background-upload-input">
                                    <div className="upload-icon-container">
                                        <IconImageRequest color="var(--color-semantic-3)"/>
                                    </div>
                                    <FileUpload
                                        uploadFile={(fileData, fileContent) => uploadReturnPortalCoverImage(fileData, fileContent as string)}
                                        buttonText={"Upload image"}
                                        readType="dataUrl"
                                        fileTypes={['image/jpeg', 'image/png', 'image/gif']}
                                        acceptedFileFormats=".jpg,.jpeg,.png,.gif"
                                        disabled={edgeCustomizationStore.isSaving()}
                                    />
                                </div>
                                {
                                    edgeCustomizationStore.returnPortalCoverImage && (
                                        <div className="background-preview-container">
                                            <FontBody2 color={FONT_COLOR.PRIMARY3_50}>
                                                Image size {edgeCustomizationStore.returnPortalCoverImageDimensions}
                                            </FontBody2>
                                            <div className="background">
                                                <img src={edgeCustomizationStore.returnPortalCoverImage} alt="background"/>
                                                <ButtonIcon
                                                    Component={IconDelete}
                                                    color="var(--color-accent-3)"
                                                    onClick={edgeCustomizationStore.deleteReturnPortalCoverImage}
                                                    disabled={edgeCustomizationStore.isSaving()}
                                                />
                                            </div>
                                        </div>
                                    )
                                }
                            </div>
                        </FormFieldLine>
                    </div>
                </ListLayout>
            </ControlGroupWithPreview>
        </Card>
    );
});

export default PortalEdgeBackgroundCard;
